<script>
import { SETUP_RESOURCES } from "@/utils/constants";

const VIDEO_THUMBNAIL = "https://fast.wistia.com/embed/medias/0npill4p1r/swatch";

const WISTIA_SCRIPTS = [
  { src: "https://fast.wistia.com/embed/medias/0npill4p1r.jsonp", async: true },
  { src: "https://fast.wistia.com/assets/external/E-v1.js", async: true },
];

const TODO_LINKS = {
  "Create a Proof": "/create-proof",
  "Share a Review Link": "/create-proof",
  "Create an Email Template": "/email-templates",
  "Send a Proof via a Workflow": "/workflows",
  "Create an Approval Checklist": "/checklists",
};

export default {
  name: "getting-started",
  props: {
    todoList: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      resources: Object.values(SETUP_RESOURCES),
      videoThumbnail: VIDEO_THUMBNAIL,
    };
  },
  mounted() {
    WISTIA_SCRIPTS.forEach((script) => {
      const scriptElement = document.createElement("script");
      Object.assign(scriptElement, script);
      document.head.appendChild(scriptElement);
    });
  },
  methods: {
    openTodo(item) {
      const link = TODO_LINKS[item.title];
      if (link) {
        this.$A.NavigateToView(link);
      }
    },
    openResource(resource) {
      // string form so the ?slug= query is parsed (object-form push drops it)
      this.$router.push(resource.link);
    },
  },
};
</script>

<template>
  <div class="getting-started">
    <div class="todo-card">
      <div class="card-head">To Do</div>
      <div class="todo-rows">
        <button
          v-for="item in todoList"
          :key="item.title"
          type="button"
          class="todo-row"
          :class="{ 'is-complete': item.completed }"
          @click="openTodo(item)"
        >
          <div v-if="!item.completed" class="todo-icon">
            <img :src="`/img/${item.icon}`" :alt="item.title" />
          </div>
          <div v-else class="todo-check">
            <img src="/img/check.svg" alt="Check" />
          </div>
          <div class="todo-text">
            <div class="todo-title" :class="{ struck: item.completed }">{{ item.title }}</div>
            <div class="todo-description" :class="{ struck: item.completed }">{{ item.description }}</div>
          </div>
          <span v-if="!item.completed" class="todo-badge">{{ item.duration }}</span>
          <span v-else class="todo-badge complete">Complete!</span>
          <svg class="todo-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
    </div>
    <div class="resources-col">
      <div class="video-card">
        <div class="video-container">
          <div class="wistia_responsive_wrapper">
            <div class="wistia_embed wistia_async_0npill4p1r seo=true videoFoam=true">
              <div class="wistia_swatch wis-container">
                <img
                  :src="videoThumbnail"
                  class="wis-thumbnail"
                  alt="wistia video thumbnail"
                  aria-hidden="true"
                  onload="this.parentNode.style.opacity=1;"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="video-description">
          <span class="video-title">5 min Ashore Overview</span>
          <span class="video-duration">5:23 min</span>
        </div>
      </div>
      <div class="resources-card">
        <div class="card-head">Setup Resources</div>
        <a
          v-for="resource in resources"
          :key="resource.title"
          :href="resource.link"
          class="resource-row"
          @click.prevent="openResource(resource)"
        >
          {{ resource.title }}
          <img src="img/review/icons/chevron-right.svg" width="20" height="20" alt="" />
        </a>
      </div>
    </div>
  </div>
</template>

<style scoped>
.getting-started {
  display: flex;
  gap: 16px;
  align-items: stretch;
}
@media (max-width: 991px) {
  .getting-started {
    flex-direction: column;
  }
}
.todo-card {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.resources-col {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 320px;
  flex-shrink: 0;
  align-self: flex-start;
}
@media (max-width: 991px) {
  .resources-col {
    width: 100%;
  }
}
.todo-card,
.resources-card,
.video-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.1);
  overflow: hidden;
}
.card-head {
  padding: 14px 20px;
  background: #fbfbfc;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}
.todo-rows {
  display: flex;
  flex-direction: column;
  flex: 1;
}
.todo-row {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  flex: 1;
  padding: 15px 20px;
  border: 0;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background-color 120ms ease;
  font-family: inherit;
}
.todo-row:hover {
  background: #f9f9fa;
}
.todo-row:hover .todo-chevron {
  color: #019256;
  transform: translateX(2px);
}
.todo-row:last-child {
  border-bottom: 0;
}
.todo-row.is-complete {
  background: rgba(1, 146, 86, 0.04);
}
.todo-row.is-complete:hover {
  background: rgba(1, 146, 86, 0.08);
}
.todo-icon,
.todo-check {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}
.todo-icon {
  border-radius: 12px;
  border: 1px dashed #b5b5b8;
  background: #f0f9f4;
}
.todo-icon img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}
.todo-check {
  border-radius: 999px;
  border: 1px solid #019256;
  background: #dff3e7;
}
.todo-check img {
  width: 20px;
  height: 20px;
}
.todo-text {
  flex: 1;
  min-width: 0;
}
.todo-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #111827;
}
.todo-description {
  font-size: 12.5px;
  font-weight: 400;
  color: #6b7280;
  line-height: 1.5;
}
.struck {
  text-decoration: line-through;
  text-decoration-thickness: 1px;
}
.todo-badge {
  flex-shrink: 0;
  padding: 2px 10px;
  border-radius: 999px;
  background: #f3f4f6;
  font-size: 11px;
  font-weight: 600;
  color: #4b5563;
  white-space: nowrap;
}
.todo-badge.complete {
  background: rgba(1, 146, 86, 0.1);
  color: #017a48;
}
.todo-chevron {
  flex-shrink: 0;
  color: #9ca3af;
  transition: color 120ms ease, transform 120ms ease;
}
.resource-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  text-decoration: none;
  transition: background-color 120ms ease;
}
.resource-row:last-child {
  border-bottom: 0;
}
.resource-row:hover {
  background: #f9f9fa;
  text-decoration: none;
  color: #019256;
}
.video-container {
  width: 100%;
}
.wistia_responsive_wrapper {
  height: 100%;
  left: 0;
  position: relative;
  top: 0;
  width: 100%;
}
.wis-container {
  height: 100%;
  left: 0;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  top: 0;
  transition: opacity 200ms;
  width: 100%;
}
.wis-thumbnail {
  filter: blur(5px);
  height: 100%;
  object-fit: contain;
  width: 100%;
}
.video-description {
  display: flex;
  flex-direction: column;
  padding: 12px 20px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}
.video-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #111827;
}
.video-duration {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}
</style>
